import { useState, type FormEvent } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { api, type ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'

// DIS-31: /reset/{token} — choose a new password. Signs you in and revokes
// every other session.
export const Route = createFileRoute('/reset/$token')({
  loader: async ({ params }) => {
    const { response } = await api.GET('/auth/reset/{token}', { params: { path: { token: params.token } } })
    return { valid: response.ok }
  },
  component: ResetPage,
})

function ResetPage() {
  const { token } = Route.useParams()
  const { valid } = Route.useLoaderData()
  const { auth } = Route.useRouteContext()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const password = String(form.get('password') ?? '')
    if (password !== String(form.get('confirm') ?? '')) {
      setError("Passwords don't match.")
      return
    }
    setBusy(true)
    setError(null)
    const { data, error: err, response } = await api.POST('/auth/reset/{token}', { params: { path: { token } }, body: { password } })
    setBusy(false)
    if (data) {
      await auth.invalidate()
      await navigate({ to: '/' })
      return
    }
    const apiErr = err as ApiError | undefined
    setError(apiErr?.fields?.password ?? apiErr?.message ?? `Couldn't reset (${response.status})`)
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-bg-muted p-6">
        <h1 className="text-lg font-semibold">Choose a new password</h1>
        {!valid ? (
          <>
            <p className="text-sm text-fg-muted">This reset link is invalid, expired, or already used.</p>
            <p className="text-sm">
              <Link to="/forgot" className="text-accent underline">
                Request a new one
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Input name="password" type="password" required minLength={12} placeholder="New password (12+ characters)" autoComplete="new-password" autoFocus />
            <Input name="confirm" type="password" required minLength={12} placeholder="Confirm new password" autoComplete="new-password" />
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? 'Saving…' : 'Set password and sign in'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
